import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Actor } from '../../shared/roles.ts';
import { type LoginResult, loginResultSchema } from '../../shared/schemas/mfa.ts';
import {
  type ResetInput,
  type ResetRequest,
  type SessionStatus,
  sessionStatusSchema,
} from '../../shared/schemas/user.ts';
import { apiRequest, describeApiFailure, NO_CONTENT, type Parser } from '../api/http.ts';
import { useToast } from '../components/Toast.tsx';
import { stopOfflineCopy } from '../offline/session.ts';

export const SESSION_QUERY_KEY = ['session'] as const;

export interface Credentials {
  readonly username: string;
  readonly password: string;
}

/**
 * What the password step answers: nothing (204, signed in) or, for an
 * account with a second factor, `{ mfaRequired: true }` beside a short
 * challenge cookie that the code step presents.
 */
const loginReply: Parser<LoginResult> = {
  parse(data: unknown): LoginResult {
    return data === undefined ? { mfaRequired: false } : loginResultSchema.parse(data);
  },
};

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => await apiRequest('/api/auth/session', sessionStatusSchema),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** The signed-in account as a shell shows it: the avatar, and the name beside it. */
export interface SessionUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly avatarUpdatedAt: string | null;
}

/**
 * Who is signed in, for the shell that draws them (the app's header, the
 * portal's). Null while the session is still being read, and once it says
 * nobody is signed in — an account with no id is not a person to show.
 */
export function sessionUser(status: SessionStatus | undefined): SessionUser | null {
  return status?.authenticated === true && status.userId !== undefined
    ? {
        id: status.userId,
        username: status.username ?? '',
        displayName: status.displayName,
        avatarUpdatedAt: status.avatarUpdatedAt,
      }
    : null;
}

/**
 * Who is acting, for the permission checks in shared/roles.ts (may this
 * person remove this note, this checklist, this preset?). Null while the
 * session is still being read, and once it says nobody is signed in; a check
 * against null is what every caller falls back to, so the answer is never a
 * guessed role. The Worker refuses the write again whatever this says.
 */
export function sessionActor(status: SessionStatus | undefined): Actor | null {
  return status?.authenticated === true && status.userId !== undefined && status.role !== undefined
    ? { id: status.userId, role: status.role }
    : null;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: Credentials) =>
      await apiRequest('/api/auth/login', loginReply, { method: 'POST', body: credentials }),
    onSuccess: (result) => {
      // Drop the cached "not signed in" answer so the next protected route
      // waits for a fresh check instead of bouncing back to the login page.
      // A sign-in that still owes a code has not opened a session yet.
      if (!result.mfaRequired) {
        queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
      }
    },
  });
}

/** The code step of a sign-in: six digits from the app, or a recovery code. */
export function useLoginMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      await apiRequest('/api/auth/login/mfa', NO_CONTENT, { method: 'POST', body: { code } });
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

/** "Forgot password": a 204 whether or not the account exists. */
export function useRequestReset() {
  return useMutation({
    mutationFn: async (input: ResetRequest) => {
      await apiRequest('/api/auth/reset/request', NO_CONTENT, { method: 'POST', body: input });
    },
  });
}

/** The reset link's form: token plus the new password. */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (input: ResetInput) => {
      await apiRequest('/api/auth/reset', NO_CONTENT, { method: 'POST', body: input });
    },
  });
}

/** What the session query holds once the person has signed out. */
export function signedOut(previous: SessionStatus | undefined): SessionStatus {
  return {
    authenticated: false,
    mail: previous?.mail ?? false,
    credentials: previous?.credentials ?? false,
    passwordChangeRequired: false,
    mfaEnabled: false,
    displayName: '',
    avatarUpdatedAt: null,
    addressLookup: previous?.addressLookup ?? false,
  };
}

/**
 * Sign out: the Worker ends the session, the offline copy on this device is
 * deleted, the session query is set to signed out at once (so the gate shows
 * the login page without waiting on a refetch), and every other cached read
 * is dropped so nothing of this account stays in memory — or on the disk —
 * for the next.
 *
 * A sign-out the server never received is not a sign-out: the session
 * cookie would still open the app on the next visit. So with no signal (the
 * auth routes are never answered from the copy) the person is told, in the
 * one toast every failed action uses, and nothing local is touched — the
 * queued edits stay to be sent, and the account stays signed in until the
 * server can end the session.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  return useMutation({
    mutationFn: async () => {
      await apiRequest('/api/auth/logout', NO_CONTENT, { method: 'POST' });
      await stopOfflineCopy();
    },
    onSuccess: () => {
      queryClient.setQueryData<SessionStatus>(SESSION_QUERY_KEY, (previous) => signedOut(previous));
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
      });
    },
    onError: (error) => {
      notify(describeApiFailure(error), 'error');
    },
  });
}
